import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChatBubble } from "./components/ChatBubble.jsx";
import { ChatNotification } from "./components/ChatNotification.jsx";
import { PushNotificationModal } from "./components/PushNotificationModal.jsx";
import { InstallPwaModal } from "./components/InstallPwaModal.jsx";
import { PendingReviewsSlider } from "./components/PendingReviewsSlider.jsx";
import { ChatWindow } from "./components/ChatWindow.jsx";
import {
    WhatsAppButton,
    buildWhatsAppUrl,
} from "./components/WhatsAppButton.jsx";

/**
 * Check if current pathname matches any of the allowed routes
 * Routes use prefix matching, except "/" which is exact match only.
 * @param {string[]} routes - Array of route prefixes (e.g., ["/shop", "/products", "/"])
 * @returns {boolean}
 */
function matchesRoute(routes) {
    if (!routes || !Array.isArray(routes) || routes.length === 0) {
        return true; // No routes specified = show on all pages
    }

    const pathname = window.location.pathname;
    return routes.some((route) => {
        // Normalize route (ensure it starts with /)
        const normalizedRoute = route.startsWith("/") ? route : `/${route}`;

        // Special case: "/" should only match exact root path
        if (normalizedRoute === "/") {
            return pathname === "/" || pathname === "";
        }

        // For other routes, use prefix matching with boundary check
        // e.g., "/shop" matches "/shop", "/shop/", "/shop/item" but not "/shopping"
        return (
            pathname === normalizedRoute ||
            pathname.startsWith(normalizedRoute + "/")
        );
    });
}

/**
 * Main App Component
 * Renders the floating support widget with chat bubble and window
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useSocket, useAutoTracker } from "./lib/useSocket.js";
import { fetchConversations } from "./lib/api.js";
import { ensureShopperSession } from "./lib/sessionSync.js";
import { useCall } from "./lib/useCall.js";
import { IncomingCallPanel } from "./components/IncomingCallPanel.jsx";

const queryClient = new QueryClient();

export function App({ routes, darkMode = false, controller, whatsapp = null }) {
    // Set when the store sends shoppers to WhatsApp instead of the on-site
    // chat. The chat window stays mounted (socket, tracking, incoming calls),
    // but the launcher and every chat.open() route to wa.me instead.
    const whatsappUrl = buildWhatsAppUrl(whatsapp);
    const [isOpen, setIsOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(() => matchesRoute(routes));
    const [unreadCount, setUnreadCount] = useState(0);
    const modalRef = useRef();
    const pwaModalRef = useRef();
    const pendingReviewsRef = useRef();

    // Get socket methods - this will share the singleton instance used by ChatWindow
    const socketApi = useSocket();
    const { on, emit } = socketApi;

    // Auto-track user interactions through the same socket connection
    useAutoTracker(socketApi);

    // Voice-call lifecycle (owned here so incoming calls ring even while the
    // chat window is closed). The remote <audio> element renders below,
    // inside the widget root (and therefore inside the Shadow DOM).
    const call = useCall(socketApi);
    const callRef = useRef(call);
    useEffect(() => {
        callRef.current = call;
    }, [call]);

    const lastStatusRef = useRef(call.status);
    useEffect(() => {
        if (call.status === lastStatusRef.current) return;
        lastStatusRef.current = call.status;
        callStatusHandlers.current.forEach((h) => {
            try {
                h(call.status);
            } catch {
                /* page code must never break the widget */
            }
        });
    }, [call.status]);

    const isOpenRef = useRef(isOpen);
    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);

    // Accepting a call also opens the chat window so the in-call overlay shows
    const handleAcceptCall = useCallback(() => {
        callRef.current.acceptCall();
        setIsOpen(true);
    }, []);

    // Declared ABOVE the controller-registration effect below: isCallEnabled
    // sits in that effect's dependency array, and dep arrays evaluate during
    // render — a later declaration is a temporal-dead-zone crash that takes the
    // whole widget down (App.jsx:184, 2026-07-13). Same lesson as the
    // dashboard's call-context TDZ the day before.
    const callApiRef = useRef(null); // ChatWindow's starter — mounted-only
    const callStatusHandlers = useRef(new Set());
    const enabledCache = useRef({ value: null, at: 0 });

    const handleCallApiReady = useCallback((api) => {
        callApiRef.current = api;
    }, []);

    // Answer "are calls enabled?" even while the chat window is closed: ask the
    // same endpoint the widget itself uses, with a short cache. Server-
    // authoritative either way — call:offer enforces the flag regardless.
    const isCallEnabled = useCallback(async () => {
        const live = callApiRef.current?.enabled;
        if (live === true || live === false) return live;

        const cached = enabledCache.current;
        if (cached.value !== null && Date.now() - cached.at < 60000) {
            return cached.value;
        }
        try {
            await ensureShopperSession();
            const res = await fetchConversations({ type: "shop_support", limit: 1 });
            const shop = res?.data?.currentShop || res?.currentShop || null;
            const value = shop ? shop.voiceCallsEnabled === true : null;
            enabledCache.current = { value, at: Date.now() };
            return value;
        } catch {
            return null; // unknown — caller decides; never throw into page code
        }
    }, []);

    useEffect(() => {
        if (controller) {
            if (modalRef.current) {
                controller.subscription = {
                    openModal: () => modalRef.current.openModal(),
                    getSubscriptionCount: () =>
                        modalRef.current.getSubscriptionCount(),
                };
            }
            if (pwaModalRef.current) {
                controller.pwa = {
                    openModal: () => pwaModalRef.current.openModal(),
                    isInstallable: () => pwaModalRef.current.isInstallable(),
                };
            }
            if (pendingReviewsRef.current) {
                controller.reviews = {
                    open: () => pendingReviewsRef.current.open(),
                    close: () => pendingReviewsRef.current.close(),
                };
            }

            const openWhatsApp = (text) => {
                window.open(
                    buildWhatsAppUrl(whatsapp, text) || whatsappUrl,
                    "_blank",
                    "noopener,noreferrer",
                );
            };

            controller.chat = {
                open: () =>
                    whatsappUrl ? openWhatsApp() : setIsOpen(true),
                close: () => setIsOpen(false),
                toggle: () =>
                    whatsappUrl
                        ? openWhatsApp()
                        : setIsOpen((prev) => !prev),
                get isOpened() {
                    return isOpenRef.current;
                },
                prefill: (opts) => {
                    if (whatsappUrl) {
                        openWhatsApp(opts?.text);
                        return;
                    }
                    setIsOpen(true);
                    // Delay prefill slightly so ChatWindow is rendered/open
                    setTimeout(() => prefillHandlerRef.current?.(opts), 50);
                },
                // Socket access
                on: (event, handler) => on(event, handler),
                emit: (event, ...args) => emit(event, ...args),
                // Voice call
                call: {
                    get status() {
                        return callRef.current.status;
                    },
                    get muted() {
                        return Boolean(callRef.current.muted);
                    },
                    get speakerOn() {
                        return Boolean(callRef.current.speakerOn);
                    },
                    getState: () => ({
                        status: callRef.current.status,
                        muted: Boolean(callRef.current.muted),
                        speakerOn: Boolean(callRef.current.speakerOn),
                    }),
                    isEnabled: () => isCallEnabled(),
                    // Opens the chat (the call UI lives there), waits for it to
                    // mount and load its shop context, then starts. Resolves
                    // {started, reason} — never rejects into page code.
                    start: async () => {
                        const enabled = await isCallEnabled();
                        if (enabled === false) {
                            return { started: false, reason: "disabled" };
                        }
                        setIsOpen(true);
                        for (let i = 0; i < 40; i++) {
                            const api = callApiRef.current;
                            if (api) {
                                const result = api.start();
                                if (result.reason !== "loading") return result;
                            }
                            await new Promise((r) => setTimeout(r, 250));
                        }
                        return { started: false, reason: "timeout" };
                    },
                    hangup: () => callRef.current.hangup(),
                    toggleMute: () => callRef.current.toggleMute(),
                    toggleSpeaker: () => callRef.current.toggleSpeaker(),
                    onStatusChange: (handler) => {
                        if (typeof handler !== "function") return () => {};
                        callStatusHandlers.current.add(handler);
                        return () => callStatusHandlers.current.delete(handler);
                    },
                },
            };
        }
    }, [controller, on, emit, isCallEnabled, whatsapp, whatsappUrl]);

    // Listen for route changes (for SPAs)
    useEffect(() => {
        const checkRoute = () => {
            setIsVisible(matchesRoute(routes));
        };

        // Check on popstate (back/forward navigation)
        window.addEventListener("popstate", checkRoute);

        // Intercept pushState/replaceState for SPA navigation
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;

        history.pushState = function (...args) {
            originalPushState.apply(this, args);
            checkRoute();
        };

        history.replaceState = function (...args) {
            originalReplaceState.apply(this, args);
            checkRoute();
        };

        return () => {
            window.removeEventListener("popstate", checkRoute);
            history.pushState = originalPushState;
            history.replaceState = originalReplaceState;
        };
    }, [routes]);

    const prefillHandlerRef = useRef(null);


    const handlePrefillReady = useCallback((handler) => {
        prefillHandlerRef.current = handler;
    }, []);

    const toggleChat = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const closeChat = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleUnreadCountChange = useCallback((count) => {
        setUnreadCount(count);
    }, []);

    // Don't render anything if route doesn't match — EXCEPT keep the call
    // audio sink + incoming ring alive while a call is active, so audio
    // doesn't cut and the ring still shows if the shopper navigates to a
    // non-matching page mid-call. (useCall runs above this return regardless.)
    if (!isVisible) {
        if (call.status === "idle") {
            return null;
        }
        return (
            <div className="support-widget-root">
                <audio
                    ref={call.attachAudioElement}
                    autoPlay
                    playsInline
                    className="tw-hidden"
                />
                <IncomingCallPanel
                    call={call}
                    darkMode={darkMode}
                    onAccept={handleAcceptCall}
                />
            </div>
        );
    }

    return (
        <QueryClientProvider client={queryClient}>
            <div className="support-widget-root">
                <PushNotificationModal ref={modalRef} darkMode={darkMode} />
                <InstallPwaModal ref={pwaModalRef} darkMode={darkMode} />
                <PendingReviewsSlider
                    ref={pendingReviewsRef}
                    darkMode={darkMode}
                />
                {/* Remote call audio — lives in the Shadow DOM; useCall attaches
                    the remote stream and replays it from user gestures */}
                <audio
                    ref={call.attachAudioElement}
                    autoPlay
                    playsInline
                    className="tw-hidden"
                />
                {/* ChatWindow is always mounted so socket/queries run on mount */}
                <ChatWindow
                    isOpen={isOpen}
                    onClose={closeChat}
                    darkMode={darkMode}
                    onUnreadCountChange={handleUnreadCountChange}
                    onPrefillReady={handlePrefillReady}
                    onCallApiReady={handleCallApiReady}
                    call={call}
                />
                <div
                    className={`tw-fixed tw-bottom-6 tw-right-6 ${
                        isOpen ? "sm:tw-hidden" : "sm:tw-block"
                    }`}
                    style={{ zIndex: 2147483646 }}
                >
                    {whatsappUrl ? (
                        <WhatsAppButton href={whatsappUrl} />
                    ) : (
                        <>
                            <ChatNotification
                                isOpen={isOpen}
                                darkMode={darkMode}
                                onClick={toggleChat}
                            />
                            <ChatBubble
                                isOpen={isOpen}
                                onClick={toggleChat}
                                darkMode={darkMode}
                                unreadCount={unreadCount}
                                call={call}
                            />
                        </>
                    )}
                </div>
                {/* Incoming-call ring — LAST child so it stacks above the open
                    ChatWindow (both use the max z-index; later DOM node wins).
                    Mounted at App level so it also rings while chat is closed. */}
                <IncomingCallPanel
                    call={call}
                    darkMode={darkMode}
                    onAccept={handleAcceptCall}
                />
            </div>
        </QueryClientProvider>
    );
}

export default App;
